import React, { useState, useEffect } from 'react';
import { getMessagesAdmin, updateMessages, MessageCenterData } from '../../services/messageCenterService';
import { toast } from 'react-toastify';

const Messages: React.FC = () => {
  const [messageData, setMessageData] = useState<MessageCenterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // State for message center texts
  const [messageCenterTexts, setMessageCenterTexts] = useState<{ [key: string]: string }>({});
  
  // State for SMS/Email table data
  const [tableData, setTableData] = useState<{
    [key: string]: {
      smsTitle: string;
      emailTitle: string;
      emailSubject: string;
      messageText: string;
    };
  }>({});

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await getMessagesAdmin();
      if (response.success) {
        setMessageData(response.data);
        
        // Set message center texts
        setMessageCenterTexts(response.data.messageCenter);
        
        // Set table data
        const newTableData: typeof tableData = {};
        Object.keys(response.data.smsMessages).forEach((key, index) => {
          const smsKey = key;
          const emailKey = `EX${index + 1}`;
          const rowKey = `${index + 1}`;
          
          newTableData[rowKey] = {
            smsTitle: response.data.smsMessages[smsKey]?.title || '',
            emailTitle: response.data.emailMessages[emailKey]?.title || '',
            emailSubject: response.data.emailMessages[emailKey]?.subject || '',
            messageText: response.data.smsMessages[smsKey]?.content || ''
          };
        });
        setTableData(newTableData);
      }
    } catch (error) {

      toast.error('خطا در دریافت پیام‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare SMS messages data
      const smsMessages: { [key: string]: { content: string } } = {};
      Object.keys(tableData).forEach((rowKey) => {
        const smsKey = `SX${rowKey}`;
        smsMessages[smsKey] = {
          content: tableData[rowKey].messageText
        };
      });
      
      // Prepare Email messages data
      const emailMessages: { [key: string]: { subject: string; content: string } } = {};
      Object.keys(tableData).forEach((rowKey) => {
        const emailKey = `EX${rowKey}`;
        emailMessages[emailKey] = {
          subject: tableData[rowKey].emailSubject,
          content: tableData[rowKey].messageText
        };
      });
      
      const updateData = {
        messageCenter: messageCenterTexts,
        smsMessages,
        emailMessages
      };
      
      const response = await updateMessages(updateData);
      if (response.success) {
        toast.success('پیام‌ها با موفقیت ذخیره شدند');
        await fetchMessages(); // Refresh data
      }
    } catch (error) {

      toast.error('خطا در ذخیره پیام‌ها');
    } finally {
      setSaving(false);
    }
  };

  const handleMessageCenterChange = (code: string, value: string) => {
    setMessageCenterTexts(prev => ({
      ...prev,
      [code]: value
    }));
  };

  const handleTableChange = (rowKey: string, field: string, value: string) => {
    setTableData(prev => ({
      ...prev,
      [rowKey]: {
        ...prev[rowKey],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center py-12">
          <div className="text-lg">در حال بارگذاری...</div>
        </div>
      </div>
    );
  }

  const messageCenterItems = [
    { code: 'MX1', title: 'خوشامدگویی ثبت نام' },
    { code: 'MX2', title: 'تغییر رمز عبور کاربر' },
    { code: 'MX3', title: 'رمز یکبار مصرف کاربر' },
    { code: 'MX4', title: 'محدوده مناطق غیر مجاز' },
    { code: 'MX5', title: 'سن غیر مجاز نمونه گیری در منزل' },
    { code: 'MX6', title: 'ثبت موفقیت آمیز درخواست' },
    { code: 'MX7', title: 'عدم تایید درخواست' },
    { code: 'MX8', title: 'تایید درخواست' },
    { code: 'MX9', title: 'تایید و دریافت ظرف' },
    { code: 'MX10', title: 'درخواست منقضی شده' },
    { code: 'MX11', title: 'ثبت درخواست تصویر جواب آزمایش' },
    { code: 'MX12', title: 'تایید درخواست تصویر جواب آزمایش' },
    { code: 'MX13', title: 'عدم تایید درخواست تصویر جواب آزمایش' },
    { code: 'MX14', title: 'ثبت درخواست پرداخت باقیمانده' },
    { code: 'MX15', title: 'تایید درخواست پرداخت باقیمانده' },
    { code: 'MX16', title: 'عدم تایید درخواست پرداخت باقیمانده' },
    { code: 'MX17', title: 'منقضی شدن درخواست' },
    { code: 'MX18', title: 'ثبت تمدید درخواست' },
    { code: 'MX19', title: 'تایید تمدید درخواست' },
    { code: 'MX20', title: 'عدم تایید تمدید درخواست' }
  ];

  const tableItems = [
    { row: '1', title: 'خوشامدگویی ثبت نام' },
    { row: '2', title: 'تغییر رمز عبور کاربر' },
    { row: '3', title: 'رمز یکبار مصرف کاربر' },
    { row: '4', title: 'تغییر رمز ادمین' },
    { row: '5', title: 'ثبت موفقیت آمیز درخواست' },
    { row: '6', title: 'عدم تایید درخواست' },
    { row: '7', title: 'تایید درخواست' },
    { row: '8', title: 'تایید و دریافت ظرف' },
    { row: '9', title: 'درخواست منقضی شده' },
    { row: '10', title: 'ثبت درخواست تصویر جواب آزمایش' },
    { row: '11', title: 'تایید درخواست تصویر جواب آزمایش' },
    { row: '12', title: 'عدم تایید درخواست تصویر جواب آزمایش' },
    { row: '13', title: 'ثبت درخواست پرداخت باقیمانده' },
    { row: '14', title: 'تایید درخواست پرداخت باقیمانده' },
    { row: '15', title: 'عدم تایید درخواست پرداخت باقیمانده' },
    { row: '16', title: 'منقضی شدن درخواست' },
    { row: '17', title: 'ثبت تمدید درخواست' },
    { row: '18', title: 'تایید تمدید درخواست' },
    { row: '19', title: 'عدم تایید تمدید درخواست' }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">مدیریت پیام‌ها</h1>
        
        {/* Message Center Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">مرکز پیام</h2>
          <div className="space-y-4">
            {messageCenterItems.map((item) => (
              <div key={item.code} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium ml-3">
                    {item.code}
                  </span>
                  <span className="font-medium text-gray-700">{item.title}</span>
                </div>
                <textarea
                  value={messageCenterTexts[item.code] || ''}
                  onChange={(e) => handleMessageCenterChange(item.code, e.target.value)}
                  className="w-full h-20 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={`متن پیام ${item.code} را وارد کنید...`}
                  dir="rtl"
                />
              </div>
            ))}
          </div>
        </div>

        {/* SMS & Email Table Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">پیامک ها و ایمیل ها</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">عنوان پیامک</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">عنوان ایمیل</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">موضوع</th>
                  <th className="py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">متن پیام</th>
                </tr>
              </thead>
              <tbody>
                {tableItems.map((item) => (
                  <tr key={item.row} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">
                      <div className="flex items-center">
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium ml-2">
                          SX{item.row}
                        </span>
                        <span>{item.title}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex items-center">
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-medium ml-2">
                          EX{item.row}
                        </span>
                        <span>{item.title}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <input
                        type="text"
                        value={tableData[item.row]?.emailSubject || ''}
                        onChange={(e) => handleTableChange(item.row, 'emailSubject', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                        placeholder="موضوع ایمیل"
                        dir="rtl"
                      />
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <textarea
                        value={tableData[item.row]?.messageText || ''}
                        onChange={(e) => handleTableChange(item.row, 'messageText', e.target.value)}
                        className="w-full h-16 p-2 border border-gray-300 rounded text-sm resize-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                        placeholder="متن پیام"
                        dir="rtl"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-3 rounded-lg font-medium text-white transition-colors ${
              saving
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {saving ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Messages; 